/**
 * GraphService — persistence + queries for the cross-domain relationship graph.
 *
 * Edges are deduped by (from, kind, to) via a deterministic id. Removal honours
 * the no-deletion posture: the edge record is preserved and a tombstone is
 * recorded, so `all()` simply filters tombstoned edges out of every query.
 */

import {
  type EntityRef,
  type GraphIndex,
  type RelationKind,
  type Relationship,
  backlinks as backlinksOf,
  buildGraphIndex,
  neighbors as neighborsOf,
  outgoing as outgoingOf,
  relationshipId,
  wouldCreateCycle,
} from '@life-os/app-core';
import type { Clock } from './clock.js';
import { type LocalStore, Collections } from './local-store.js';

export class GraphCycleError extends Error {
  constructor(kind: RelationKind) {
    super(`Adding this "${kind}" link would create a cycle.`);
    this.name = 'GraphCycleError';
  }
}

interface Tombstone {
  readonly id: string;
}

export class GraphService {
  constructor(
    private readonly store: LocalStore,
    private readonly clock: Clock,
  ) {}

  /** Create (or idempotently re-affirm) a typed edge. Rejects cycles on acyclic kinds. */
  async link(from: EntityRef, to: EntityRef, kind: RelationKind, note: string | null = null): Promise<Relationship> {
    const existing = await this.all();
    if (wouldCreateCycle(existing, from, to, kind)) {
      throw new GraphCycleError(kind);
    }
    const id = relationshipId(from, kind, to);
    const rel: Relationship = { id, from, to, kind, createdAt: this.clock.nowIso(), note };
    await this.store.put(Collections.relationships, id, rel);
    // Re-linking clears any prior tombstone.
    await this.store.put(Collections.relationshipTombstones, id, { id, active: true });
    return rel;
  }

  /** Tombstone an edge so it disappears from queries without deleting the record. */
  async unlink(id: string): Promise<void> {
    await this.store.put(Collections.relationshipTombstones, id, { id, active: false });
  }

  /** All live edges (tombstoned edges excluded). */
  async all(): Promise<readonly Relationship[]> {
    const [rels, tombs] = await Promise.all([
      this.store.list<Relationship>(Collections.relationships),
      this.store.list<{ id: string; active: boolean }>(Collections.relationshipTombstones),
    ]);
    const dead = new Set(tombs.filter((t) => t.active === false).map((t) => t.id));
    return rels.filter((r) => !dead.has(r.id));
  }

  async index(): Promise<GraphIndex> {
    return buildGraphIndex(await this.all());
  }

  async outgoing(ref: EntityRef): Promise<readonly Relationship[]> {
    return outgoingOf(await this.index(), ref);
  }

  async backlinks(ref: EntityRef): Promise<readonly Relationship[]> {
    return backlinksOf(await this.index(), ref);
  }

  async neighbors(ref: EntityRef): Promise<readonly EntityRef[]> {
    return neighborsOf(await this.index(), ref);
  }
}

export type { Tombstone };
